/** Browser-side error reporter: breadcrumbs (clicks, navigation, API calls) + crash reports to /api/errors/report. */
const MAX_CRUMBS = 20;
const crumbs = [];
const recent = new Map();
let sentThisMinute = 0;
let minuteStart = Date.now();

export function crumb(type, data = {}) {
  crumbs.push({ t: new Date().toISOString(), type, ...data });
  if (crumbs.length > MAX_CRUMBS) crumbs.shift();
}

function csrf() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? m[1] : "";
}

export async function reportError(kind, err, extra = {}) {
  const message = String(err?.message || err || "Unknown error").slice(0, 500);
  if (!message || message === "Script error.") return;
  const key = `${kind}|${message}`;
  const now = Date.now();
  if (now - minuteStart > 60_000) { minuteStart = now; sentThisMinute = 0; }
  if ((recent.get(key) || 0) > now - 60_000 || sentThisMinute >= 10) return;
  recent.set(key, now);
  sentThisMinute += 1;
  const payload = {
    kind, message,
    stack: String(err?.stack || "").slice(0, 8000),
    page: window.location.pathname + window.location.search,
    breadcrumbs: crumbs.slice(),
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ...extra,
  };
  try {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/errors/report`, {
      method: "POST", credentials: "include", keepalive: true,
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
      body: JSON.stringify(payload),
    });
  } catch { /* reporting must never throw */ }
}

// Known non-bugs: layout observer chatter, stale-build chunk loads, Outlook SafeLinks / Android WebView host-bridge noise.
const IGNORE = [/ResizeObserver loop/, /Loading (CSS )?chunk .* failed/i, /Failed to fetch dynamically imported module/i,
  /Object Not Found Matching Id/i, /Java object is gone/i, /window\.webkit\.messageHandlers/i];

export function initErrorReporter() {
  if (window.__kinglyErrorsInit) return;
  window.__kinglyErrorsInit = true;
  window.addEventListener("error", (e) => {
    const msg = e.message || String(e.error || "");
    if (IGNORE.some((rx) => rx.test(msg))) return;
    reportError("js", e.error || msg, { source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : "" });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    if (r?.isAxiosError) return; // the server already recorded that failure
    if (IGNORE.some((rx) => rx.test(String(r?.message || r || "")))) return;
    reportError("promise", r);
  });
  document.addEventListener("click", (e) => {
    const el = e.target?.closest?.("button, a, [role=button], [data-testid], input, select");
    if (!el) return;
    const label = el.getAttribute("data-testid") || (el.textContent || "").trim().slice(0, 40) || el.tagName.toLowerCase();
    crumb("click", { target: label });
  }, true);
  const push = window.history.pushState;
  window.history.pushState = function (...args) {
    push.apply(this, args);
    crumb("nav", { to: window.location.pathname });
  };
  window.addEventListener("popstate", () => crumb("nav", { to: window.location.pathname }));
  crumb("nav", { to: window.location.pathname });
}
